/**
 * Helper functions for meal planner drag and drop functionality
 * 
 * These helpers generate unique IDs and data objects for use with @dnd-kit/react
 */

import { isValidDate } from "../../../../../utils/nutrition-util";

const isFoodDragType = (type) => type === "food" || type === "verified-food";

/**
 * Generate a unique draggable ID for a meal
 * @param {string} planMode - "simplified" or "detailed"
 * @param {Object} day - Day object containing date, dayType, optionId
 * @param {string} mealType - Type of meal (e.g., "Breakfast", "Lunch")
 * @returns {string} Unique draggable ID
 */
export const getMealDraggableId = (planMode, day, mealType) => {
  if (planMode === "simplified") {
    const { dayType, optionId } = day;
    return `meal-drag-${planMode}-${dayType}-${optionId}-${mealType}`;
  }
  // Detailed
  const { date } = day;
  return `meal-drag-${planMode}-${date}-${mealType}`;
};

/**
 * Generate a unique droppable ID for a meal
 * @param {string} planMode - "simplified" or "detailed"
 * @param {Object} day - Day object containing date, dayType, optionId
 * @param {string} mealType - Type of meal (e.g., "Breakfast", "Lunch")
 * @returns {string} Unique droppable ID
 */
export const getMealDroppableId = (planMode, day, mealType) => {
  if (planMode === "simplified") {
    const { dayType, optionId } = day;
    return `meal-drop-${planMode}-${dayType}-${optionId}-${mealType}`;
  }
  // Detailed
  const { date } = day;
  return `meal-drop-${planMode}-${date}-${mealType}`;
};

/**
 * Generate data object for drag and drop operations
 * Contains all necessary information to identify source and target meals
 * @param {string} planMode - "simplified" or "detailed"
 * @param {Object} day - Day object containing date, dayType, optionId
 * @param {string} mealType - Type of meal (e.g., "Breakfast", "Lunch")
 * @param {number} week - Week number (for detailed plans)
 * @returns {Object} Data object for drag/drop
 */
export const getMealDragData = (planMode, day, mealType, week) => {
  const baseData = {
    planMode,
    mealType,
  };

  if (planMode === "simplified") {
    const { dayType, optionId } = day;
    return {
      ...baseData,
      dayType,
      optionId,
    };
  }

  // Detailed
  const { date } = day;
  // For templates, format date as "Week X Day Y" (e.g., "Week 1 Day 1")
  // For real meal plans, use the date as-is (it's already a valid date string)
  const formattedDate = isValidDate(date) ? date : `Week ${week} ${date}`;
  return {
    ...baseData,
    date: formattedDate,
    weekIndex: week - 1, // Convert to 0-based index
  };
};

/**
 * Generate a unique draggable ID for a single food item
 * @param {string} planMode - "simplified" or "detailed"
 * @param {Object} day - Day object containing date, dayType, optionId
 * @param {string} mealType - Type of meal (e.g., "Breakfast", "Lunch")
 * @param {string} foodId - Food ID (_id)
 * @param {number} [index] - Index in the meal's foods list (required when same food can appear multiple times)
 * @returns {string} Unique draggable ID
 */
export const getFoodDraggableId = (planMode, day, mealType, foodId, index = 0) => {
  const suffix = index == null ? "" : `-${index}`;
  if (planMode === "simplified") {
    const { dayType, optionId } = day;
    return `food-drag-${planMode}-${dayType}-${optionId}-${mealType}-${foodId}${suffix}`;
  }
  // Detailed
  const { date } = day;
  return `food-drag-${planMode}-${date}-${mealType}-${foodId}${suffix}`;
};

/**
 * Generate data object for food drag and drop operations
 * @param {string} planMode - "simplified" or "detailed"
 * @param {Object} day - Day object containing date, dayType, optionId
 * @param {string} mealType - Type of meal (e.g., "Breakfast", "Lunch")
 * @param {Object} food - Food object
 * @param {number} week - Week number (for detailed plans)
 * @param {string} foodMealPlanId - ID of the food in the meal plan
 * @param {number} [foodIndex] - Index in the meal's foods array (used when same food appears multiple times)
 * @returns {Object} Data object for food drag/drop
 */
export const getFoodDragData = (planMode, day, mealType, food, week, foodMealPlanId, foodIndex) => {
  if (!foodMealPlanId) return null;

  const baseData = {
    type: "food",
    planMode,
    mealType,
    foodId: String(foodMealPlanId),
    foodData: food,
    ...(foodIndex != null && { foodIndex }),
  };

  if (planMode === "simplified") {
    const { dayType, optionId } = day;
    return {
      ...baseData,
      dayType,
      optionId,
    };
  }

  // Detailed
  const { date } = day;
  const formattedDate = isValidDate(date) ? date : `Week ${week} ${date}`;
  return {
    ...baseData,
    date: formattedDate,
    weekIndex: week - 1,
  };
};

/**
 * Generate data object for custom meal (grouped) drag and drop operations
 * Moves all foods in the custom meal
 * @param {string} planMode - "simplified" or "detailed"
 * @param {Object} day - Day object containing date, dayType, optionId
 * @param {string} mealType - Type of meal (e.g., "Breakfast", "Lunch")
 * @param {Object} customMeal - Custom meal object with _id
 * @param {Array} foodsOfMeal - Array of food objects in this custom meal
 * @param {number} week - Week number (for detailed plans)
 * @returns {Object} Data object for custom meal drag/drop
 */
export const getCustomMealDragData = (planMode, day, mealType, customMeal, foodsOfMeal, week) => {
  const mealId = customMeal?._id || customMeal?.id;
  if (!mealId || !Array.isArray(foodsOfMeal)) return null;

  // Prefer subdocument _id so the backend finds the exact entry; fallback to catalog id for compatibility
  const foodIds = foodsOfMeal
    .map(f => f._id || f.foodId?._id || f.id || f.foodId?.id)
    .filter(Boolean)
    .map(id => id.toString());

  if (foodIds.length === 0) return null;

  const baseData = {
    type: "customMeal",
    planMode,
    mealType,
    customMealId: mealId.toString(),
    foodIds,
    meal: customMeal, // Include meal object for overlay rendering
    foodsOfMeal: foodsOfMeal, // Include foods array for overlay rendering
  };

  if (planMode === "simplified") {
    const { dayType, optionId } = day;
    return {
      ...baseData,
      dayType,
      optionId,
    };
  }

  // Detailed
  const { date } = day;
  const formattedDate = isValidDate(date) ? date : `Week ${week} ${date}`;
  return {
    ...baseData,
    date: formattedDate,
    weekIndex: week - 1,
  };
};

/**
 * Generate a unique draggable ID for a custom meal (grouped)
 * @param {string} planMode - "simplified" or "detailed"
 * @param {Object} day - Day object containing date, dayType, optionId
 * @param {string} mealType - Type of meal (e.g., "Breakfast", "Lunch")
 * @param {string} customMealId - Custom meal ID (_id)
 * @returns {string} Unique draggable ID
 */
export const getCustomMealDraggableId = (planMode, day, mealType, customMealId) => {
  if (planMode === "simplified") {
    const { dayType, optionId } = day;
    return `custom-meal-drag-${planMode}-${dayType}-${optionId}-${mealType}-${customMealId}`;
  }
  // Detailed
  const { date } = day;
  return `custom-meal-drag-${planMode}-${date}-${mealType}-${customMealId}`;
};

/**
 * Check if two meals are the same (same meal, same day/option)
 * Used to prevent moving a meal to itself
 * @param {Object} source - Source meal drag data
 * @param {Object} target - Target meal drag data
 * @returns {boolean} True if meals are the same
 */
export const isSameMeal = (source, target) => {
  if (source.planMode !== target.planMode) return false;
  if (source.mealType !== target.mealType) return false;

  if (source.planMode === "simplified") {
    return (
      source.dayType === target.dayType &&
      source.optionId === target.optionId
    );
  }

  // Detailed
  return source.date === target.date;
};

/**
 * Find meal location in detailed meal plan structure
 * @param {Array} weeks - Array of weeks from meal plan
 * @param {string|number} date - Date to find (can be "Day X" or "Week X Day Y" format)
 * @param {string} mealType - Meal type to find
 * @returns {Object|null} Object with meal, weekIndex, dayIndex, or null if not found
 */
const findMealInDetailedPlan = (weeks, date, mealType) => {
  // Normalize date: if it's "Week X Day Y", extract just "Day Y" for comparison
  let normalizedDate = String(date).trim();
  const weekDayMatch = normalizedDate.match(/Week\s+\d+\s+(Day\s+\d+)/i);
  if (weekDayMatch) {
    normalizedDate = weekDayMatch[1].trim(); // Extract "Day X" part
  }

  for (let weekIdx = 0; weekIdx < weeks.length; weekIdx++) {
    const week = weeks[weekIdx];
    for (let dayIdx = 0; dayIdx < week.days.length; dayIdx++) {
      const day = week.days[dayIdx];
      // For templates, if day.date is null/undefined, generate "Day X" from index (1-based)
      let dayDateStr = day.date;
      if (dayDateStr == null || dayDateStr === 'null' || dayDateStr === 'undefined') {
        dayDateStr = `Day ${dayIdx + 1}`;
      } else {
        dayDateStr = String(dayDateStr).trim();
      }
      
      // Compare normalized dates (case-insensitive)
      if (dayDateStr.toLowerCase() === normalizedDate.toLowerCase()) {
        const meal = day.meals.find((m) => m.type === mealType);
        if (meal) {
          return { meal, weekIndex: weekIdx, dayIndex: dayIdx };
        }
      }
    }
  }
  
  return null;
};

/**
 * Update meal plan optimistically for Detailed mode
 * Merges foods from source meal to target meal and clears source meal
 * @param {Object} mealPlan - Current meal plan object
 * @param {Object} sourceData - Source meal data (date, mealType, weekIndex)
 * @param {Object} targetData - Target meal data (date, mealType, weekIndex)
 * @returns {Object} Updated meal plan (deep copy)
 */
export const updateMealPlanDetailedOptimistic = (mealPlan, sourceData, targetData) => {
  // Validate inputs
  if (!mealPlan || !sourceData || !targetData) {
    return mealPlan;
  }

  // Validate meal plan structure
  if (!Array.isArray(mealPlan.weeks)) {
    return mealPlan;
  }

  const updatedMealPlan = structuredClone(mealPlan); // Deep copy
  const { weeks = [] } = updatedMealPlan;

  // Find source and target meals
  const sourceLocation = findMealInDetailedPlan(weeks, sourceData.date, sourceData.mealType);
  const targetLocation = findMealInDetailedPlan(weeks, targetData.date, targetData.mealType);

  if (!sourceLocation || !targetLocation) {
    return mealPlan; // Return original if not found
  }

  // Validate that source meal has foods (edge case: empty meal)
  const sourceFoods = sourceLocation.meal.foods || [];
  if (!Array.isArray(sourceFoods) || sourceFoods.length === 0) {
    // Source meal is empty, nothing to move
    return mealPlan;
  }

  const targetFoods = targetLocation.meal.foods || [];
  if (!Array.isArray(targetFoods)) {
    return mealPlan; // Invalid structure
  }

  // Find target meal in updated plan to update
  const targetMeal = updatedMealPlan.weeks[targetLocation.weekIndex].days[targetLocation.dayIndex].meals.find(
    (m) => m.type === targetData.mealType
  );
  const sourceMeal = updatedMealPlan.weeks[sourceLocation.weekIndex].days[sourceLocation.dayIndex].meals.find(
    (m) => m.type === sourceData.mealType
  );

  if (!targetMeal || !sourceMeal) {
    return mealPlan; // Safety check
  }

  // Merge foods: add source foods to target, then clear source
  targetMeal.foods = [...targetFoods, ...sourceFoods];
  sourceMeal.foods = [];

  return updatedMealPlan;
};

/**
 * Update meal plan optimistically for Simplified mode
 * Merges foods from source meal to target meal and clears source meal
 * @param {Object} mealPlan - Current meal plan object
 * @param {Object} sourceData - Source meal data (dayType, optionId, mealType)
 * @param {Object} targetData - Target meal data (dayType, optionId, mealType)
 * @returns {Object} Updated meal plan (deep copy)
 */
export const updateMealPlanSimplifiedOptimistic = (mealPlan, sourceData, targetData) => {
  // Validate inputs
  if (!mealPlan || !sourceData || !targetData) {
    return mealPlan;
  }

  // Validate meal plan structure
  if (!Array.isArray(mealPlan.trainingDayOptions) || !Array.isArray(mealPlan.restDayOptions)) {
    return mealPlan;
  }

  // Find options by optionId
  const sourceOptions = sourceData.dayType === "training"
    ? mealPlan.trainingDayOptions
    : mealPlan.restDayOptions;
  const targetOptions = targetData.dayType === "training"
    ? mealPlan.trainingDayOptions
    : mealPlan.restDayOptions;

  const sourceOption = sourceOptions.find(
    (opt) => opt.optionId?.toString() === sourceData.optionId?.toString()
  );
  const targetOption = targetOptions.find(
    (opt) => opt.optionId?.toString() === targetData.optionId?.toString()
  );

  if (!sourceOption || !targetOption) {
    return mealPlan; // Options not found
  }

  const updatedMealPlan = structuredClone(mealPlan); // Deep copy
  const { trainingDayOptions = [], restDayOptions = [] } = updatedMealPlan;

  // Get source and target options arrays
  const updatedSourceOptions = sourceData.dayType === "training" ? trainingDayOptions : restDayOptions;
  const updatedTargetOptions = targetData.dayType === "training" ? trainingDayOptions : restDayOptions;

  // Find source and target meals in the cloned structure
  const updatedSourceOption = updatedSourceOptions.find(
    (opt) => opt.optionId?.toString() === sourceData.optionId?.toString()
  );
  const updatedTargetOption = updatedTargetOptions.find(
    (opt) => opt.optionId?.toString() === targetData.optionId?.toString()
  );

  if (!updatedSourceOption || !updatedTargetOption || !Array.isArray(updatedSourceOption.meals) || !Array.isArray(updatedTargetOption.meals)) {
    return mealPlan; // Return original if not found or invalid structure
  }

  const sourceMeal = updatedSourceOption.meals.find((m) => m.type === sourceData.mealType);
  const targetMeal = updatedTargetOption.meals.find((m) => m.type === targetData.mealType);

  if (!sourceMeal || !targetMeal) {
    return mealPlan; // Return original if not found
  }

  // Validate that source meal has foods (edge case: empty meal)
  const sourceFoods = sourceMeal.foods || [];
  if (!Array.isArray(sourceFoods) || sourceFoods.length === 0) {
    // Source meal is empty, nothing to move
    return mealPlan;
  }

  const targetFoods = targetMeal.foods || [];
  if (!Array.isArray(targetFoods)) {
    return mealPlan; // Invalid structure
  }

  // Merge foods: add source foods to target, then clear source
  targetMeal.foods = [...targetFoods, ...sourceFoods];
  sourceMeal.foods = [];

  return updatedMealPlan;
};

/**
 * Validate source and target data for Detailed mode
 * @param {Object} sourceData - Source meal drag data
 * @param {Object} targetData - Target meal drag data
 * @returns {boolean} True if valid
 */
const validateDetailedData = (sourceData, targetData) => {
  return (
    sourceData.date != null &&
    sourceData.mealType &&
    targetData.date != null &&
    targetData.mealType &&
    sourceData.planMode === "detailed" &&
    targetData.planMode === "detailed"
  );
};

/**
 * Validate source and target data for Simplified mode
 * @param {Object} sourceData - Source meal drag data
 * @param {Object} targetData - Target meal drag data
 * @returns {boolean} True if valid
 */
const validateSimplifiedData = (sourceData, targetData) => {
  const validDayTypes = new Set(["training", "rest"]);
  return (
    sourceData.planMode === "simplified" &&
    targetData.planMode === "simplified" &&
    sourceData.dayType &&
    targetData.dayType &&
    validDayTypes.has(sourceData.dayType) &&
    validDayTypes.has(targetData.dayType) &&
    sourceData.optionId &&
    targetData.optionId &&
    sourceData.mealType &&
    targetData.mealType
  );
};

/**
 * Build request body for meal movement based on planMode
 * @param {Object} sourceData - Source meal drag data
 * @param {Object} targetData - Target meal drag data
 * @param {boolean} isFromNutritionPlan - Whether this is from a nutrition plan
 * @param {string} type - Operation type: "move" (default) or "copy"
 * @returns {Object|null} Request body or null if planMode is invalid or data is invalid
 */
const buildMealMoveRequestBody = (sourceData, targetData, isFromNutritionPlan, type = "move") => {
  if (sourceData.planMode === "detailed") {
    if (!validateDetailedData(sourceData, targetData)) {
      return null;
    }

    const { date: sourceDate, mealType: sourceMealType } = sourceData;
    const { date: targetDate, mealType: targetMealType } = targetData;

    return {
      sourceDate,
      sourceMealType,
      targetDate,
      targetMealType,
      isTemplate: !isFromNutritionPlan,
      type,
    };
  }

  if (sourceData.planMode === "simplified") {
    if (!validateSimplifiedData(sourceData, targetData)) {
      return null;
    }

    const {
      dayType: sourceDayType,
      optionId: sourceOptionId,
      mealType: sourceMealType,
    } = sourceData;
    const {
      dayType: targetDayType,
      optionId: targetOptionId,
      mealType: targetMealType,
    } = targetData;

    return {
      planMode: "simplified",
      sourceDayType,
      sourceOptionId,
      sourceMealType,
      targetDayType,
      targetOptionId,
      targetMealType,
      type,
    };
  }

  return null;
};

/**
 * Update meal plan optimistically when moving a single food (Detailed or Simplified)
 * Removes the food from source meal and adds it to target meal (clears mealId if grouped)
 * @param {Object} mealPlan - Current meal plan object
 * @param {Object} sourceData - Source food drag data (type: 'food', foodId, date/dayType/optionId, mealType)
 * @param {Object} targetData - Target meal drag data
 * @returns {Object} Updated meal plan (deep copy)
 */
export const updateMealPlanForFoodMove = (mealPlan, sourceData, targetData) => {
  if (!mealPlan || !sourceData || !targetData) return mealPlan;
  if (isFoodDragType(sourceData.type) && !sourceData.foodId) return mealPlan;
  if (sourceData.type === "customMeal" && (!sourceData.foodIds || !sourceData.foodIds.length)) return mealPlan;
  if (!isFoodDragType(sourceData.type) && sourceData.type !== "customMeal") return mealPlan;

  const idsToMove =
    sourceData.type === "customMeal"
      ? sourceData.foodIds.map((id) => id.toString())
      : [sourceData.foodId.toString()];
  const idSet = new Set(idsToMove);

  if (sourceData.planMode === "detailed") {
    if (!Array.isArray(mealPlan.weeks)) return mealPlan;
    const sourceLocation = findMealInDetailedPlan(mealPlan.weeks, sourceData.date, sourceData.mealType);
    const targetLocation = findMealInDetailedPlan(mealPlan.weeks, targetData.date, targetData.mealType);
    if (!sourceLocation || !targetLocation) return mealPlan;

    // Clone only the week(s) that contain source/target to keep optimistic update fast
    const weekIndicesToClone = new Set([sourceLocation.weekIndex, targetLocation.weekIndex]);
    const weeks = mealPlan.weeks.map((week, wi) =>
      weekIndicesToClone.has(wi) ? structuredClone(week) : week
    );

    const sourceMeal = weeks[sourceLocation.weekIndex].days[sourceLocation.dayIndex].meals.find(
      (m) => m.type === sourceData.mealType
    );
    const targetMeal = weeks[targetLocation.weekIndex].days[targetLocation.dayIndex].meals.find(
      (m) => m.type === targetData.mealType
    );
    if (!sourceMeal || !targetMeal) return mealPlan;

    const sourceFoods = sourceMeal.foods || [];
    const foodsToMove = sourceFoods.filter(
      (f) => idSet.has((f._id?.toString?.() || f._id) ?? "")
    );
    if (foodsToMove.length === 0) return mealPlan;

    const remaining = sourceFoods.filter(
      (f) => !idSet.has((f._id?.toString?.() || f._id) ?? "")
    );
    const cleanedMoved = foodsToMove.map((f) => {
      const cleaned = { ...f };
      if (cleaned.mealId) delete cleaned.mealId;
      return cleaned;
    });

    const targetFoods = Array.isArray(targetMeal.foods) ? targetMeal.foods : [];
    targetMeal.foods = [...targetFoods, ...cleanedMoved];
    sourceMeal.foods = remaining;

    return { ...mealPlan, weeks };
  }

  if (sourceData.planMode === "simplified") {
    if (!Array.isArray(mealPlan.trainingDayOptions) || !Array.isArray(mealPlan.restDayOptions)) {
      return mealPlan;
    }
    const updatedMealPlan = structuredClone(mealPlan);
    const sourceOptions = sourceData.dayType === "training"
      ? updatedMealPlan.trainingDayOptions
      : updatedMealPlan.restDayOptions;
    const targetOptions = targetData.dayType === "training"
      ? updatedMealPlan.trainingDayOptions
      : updatedMealPlan.restDayOptions;

    const sourceOption = sourceOptions.find(
      (opt) => opt.optionId?.toString() === sourceData.optionId?.toString()
    );
    const targetOption = targetOptions.find(
      (opt) => opt.optionId?.toString() === targetData.optionId?.toString()
    );
    if (!sourceOption || !targetOption || !sourceOption.meals || !targetOption.meals) return mealPlan;

    const sourceMeal = sourceOption.meals.find((m) => m.type === sourceData.mealType);
    const targetMeal = targetOption.meals.find((m) => m.type === targetData.mealType);
    if (!sourceMeal || !targetMeal) return mealPlan;

    const sourceFoods = sourceMeal.foods || [];
    const foodsToMove = sourceFoods.filter(
      (f) => idSet.has((f._id?.toString?.() || f._id) ?? "")
    );
    if (foodsToMove.length === 0) return mealPlan;

    const remaining = sourceFoods.filter(
      (f) => !idSet.has((f._id?.toString?.() || f._id) ?? "")
    );
    const cleanedMoved = foodsToMove.map((f) => {
      const cleaned = { ...f };
      if (cleaned.mealId) delete cleaned.mealId;
      return cleaned;
    });

    const targetFoods = Array.isArray(targetMeal.foods) ? targetMeal.foods : [];
    targetMeal.foods = [...targetFoods, ...cleanedMoved];
    sourceMeal.foods = remaining;

    return updatedMealPlan;
  }

  return mealPlan;
};

/**
 * Build request body for moving a single food or custom meal between meals
 * @param {Object} sourceData - Source food/customMeal drag data (type: 'food' or 'customMeal', foodId(s), date/dayType/optionId, mealType)
 * @param {Object} targetData - Target meal drag data
 * @param {boolean} isFromNutritionPlan - Whether this is from a nutrition plan (affects isTemplate in Detailed)
 * @returns {Object|null} Request body or null if invalid
 */
const buildFoodMoveRequestBody = (sourceData, targetData, isFromNutritionPlan) => {
  if (!isFoodDragType(sourceData.type) && sourceData.type !== "customMeal") return null;

  // For single food, use foodId; for customMeal, use foodIds array
  const foodIds = sourceData.type === "customMeal" 
    ? sourceData.foodIds 
    : [sourceData.foodId.toString()];
  
  if (!foodIds || foodIds.length === 0) return null;

  const sourceMealType = sourceData.mealType;
  const targetMealType = targetData.mealType;

  if (sourceData.planMode === "detailed") {
    if (!validateDetailedData(sourceData, targetData)) {
      return null;
    }

    const body = {
      sourceDate: sourceData.date,
      sourceMealType,
      targetDate: targetData.date,
      targetMealType,
      isTemplate: !isFromNutritionPlan,
      foodIds,
    };
    if (isFoodDragType(sourceData.type) && sourceData.foodIndex != null) {
      body.foodEntryIndex = sourceData.foodIndex;
    }
    // Add customMealId if moving a grouped meal (to preserve mealId)
    if (sourceData.type === "customMeal" && sourceData.customMealId) {
      body.customMealId = sourceData.customMealId;
    }

    return body;
  }

  if (sourceData.planMode === "simplified") {
    if (!validateSimplifiedData(sourceData, targetData)) {
      return null;
    }

    const body = {
      planMode: "simplified",
      sourceDayType: sourceData.dayType,
      sourceOptionId: sourceData.optionId,
      sourceMealType,
      targetDayType: targetData.dayType,
      targetOptionId: targetData.optionId,
      targetMealType,
      foodIds,
    };
    if (isFoodDragType(sourceData.type) && sourceData.foodIndex != null) {
      body.foodEntryIndex = sourceData.foodIndex;
    }

    // Add customMealId if moving a grouped meal (to preserve mealId)
    if (sourceData.type === "customMeal" && sourceData.customMealId) {
      body.customMealId = sourceData.customMealId;
    }

    return body;
  }

  return null;
};

/**
 * Perform optimistic update if callbacks are provided
 * @param {Object} currentMealPlan - Current meal plan
 * @param {Object} sourceData - Source meal drag data
 * @param {Object} targetData - Target meal drag data
 * @param {Function} onOptimisticUpdate - Callback to update UI
 * @returns {Object|null} Original meal plan for potential revert
 */
const performOptimisticUpdate = (currentMealPlan, sourceData, targetData, onOptimisticUpdate) => {
  if (!onOptimisticUpdate || !currentMealPlan) {
    return null;
  }

  const originalMealPlan = structuredClone(currentMealPlan);
  let updatedMealPlan;

  if (isFoodDragType(sourceData.type) || sourceData.type === "customMeal") {
    updatedMealPlan = updateMealPlanForFoodMove(currentMealPlan, sourceData, targetData);
  } else if (sourceData.planMode === "detailed") {
    updatedMealPlan = updateMealPlanDetailedOptimistic(currentMealPlan, sourceData, targetData);
  } else if (sourceData.planMode === "simplified") {
    updatedMealPlan = updateMealPlanSimplifiedOptimistic(currentMealPlan, sourceData, targetData);
  } else {
    updatedMealPlan = currentMealPlan;
  }

  onOptimisticUpdate(updatedMealPlan);
  return originalMealPlan;
};

/**
 * Create a drag and drop handler for meal movement
 * Handles both Detailed and Simplified meal plan modes
 * Supports optimistic UI updates with error handling
 * 
 * @param {Function} api - API function from Rill context
 * @param {string} mealPlanId - ID of the meal plan
 * @param {string} athlete - Athlete ID (for proxyAthlete query param)
 * @param {boolean} isFromNutritionPlan - Whether this is from a nutrition plan (affects isTemplate in Detailed mode)
 * @param {Object} options - Additional options
 * @param {Function} options.onOptimisticUpdate - Callback to update UI optimistically (receives updated mealPlan)
 * @param {Function} options.onRevert - Callback to revert optimistic update if API fails (receives original mealPlan)
 * @param {Object} options.currentMealPlan - Current meal plan object (for optimistic updates)
 * @param {string} options.type - Operation type: "move" (default) or "copy"
 * @param {Function} options.flash - Flash function to show error messages (from locals.flash)
 * @param {Function} options.submit - Submit function to refresh page after error (from submit-form)
 * @returns {Function} Handler function that can be used with DragDropProvider's onDragEnd prop
 * 
 * @example
 * const handleDrop = createMealDropHandler(
 *   api, 
 *   mealPlanId, 
 *   athlete, 
 *   isFromNutritionPlan,
 *   {
 *     onOptimisticUpdate: (updatedPlan) => setMealPlan(updatedPlan),
 *     onRevert: (originalPlan) => setMealPlan(originalPlan),
 *     currentMealPlan: mealPlan,
 *     type: "move",
 *     flash: locals.flash,
 *     submit: submit
 *   }
 * );
 * <DragDropProvider onDragEnd={handleDrop}>...</DragDropProvider>
 */
export const createMealDropHandler = (
  api,
  mealPlanId,
  athlete,
  isFromNutritionPlan = false,
  options = {}
) => {
  const {
    onOptimisticUpdate = null,
    onRevert = null,
    currentMealPlan = null,
    type = "move",
    flash = null,
    submit = null
  } = options;
  return async ({ operation }) => {
    /**
     * operation.source: The element that was DRAGGED
     * operation.target: The element on which it was DROPPED
     * 
     * If target is null, it means it was dropped outside a valid area (no droppable)
     */
    // Validate required parameters
    if (!api) {
      console.warn("API function is required for drag and drop");
      return;
    }

    if (!mealPlanId) {
      console.warn("Meal plan ID is required for drag and drop");
      return;
    }

    // If no drop target, do nothing (dropped outside a valid meal)
    if (!operation?.target) {
      return;
    }

    // Get data from source and target elements
    const sourceData = operation.source?.data;
    const targetData = operation.target?.data;

    // Validate we have required data
    if (!sourceData || !targetData) {
      return;
    }

    // Validate planMode matches between source and target
    if (sourceData.planMode !== targetData.planMode) {
      return; // Cannot move between different plan modes
    }

    // Prevent moving meal to itself (check IDs)
    if (operation.source.id === operation.target.id) {
      return;
    }

    // Prevent moving meal/food to itself (check data content)
    // For foods and customMeals, check if same meal; for meals, use isSameMeal
    if (isFoodDragType(sourceData.type) || sourceData.type === "customMeal") {
      if (isSameMeal(sourceData, targetData)) {
        return; // Cannot move food/customMeal to same meal
      }
    } else {
      if (isSameMeal(sourceData, targetData)) {
        return;
      }
    }

    // Build request body: food/customMeal move (foodIds) or meal move (full meal)
    const isFoodOrCustomMealMove = isFoodDragType(sourceData.type) || sourceData.type === "customMeal";
    const body = isFoodOrCustomMealMove
      ? buildFoodMoveRequestBody(sourceData, targetData, isFromNutritionPlan)
      : buildMealMoveRequestBody(sourceData, targetData, isFromNutritionPlan, type);
    if (!body) {
      return; // Invalid planMode or invalid data
    }

    const runUpdateAndApi = () => {
      const originalMealPlan = performOptimisticUpdate(
        currentMealPlan,
        sourceData,
        targetData,
        onOptimisticUpdate
      );

      const query = {
        ...(athlete && { proxyAthlete: athlete }),
      };

      api(`move-meal-meal-plan/${mealPlanId}`, {
        method: "PUT",
        query,
        body,
      })
        .then(() => {})
        .catch((error) => {
          console.error("Error moving meal:", error);
          if (onRevert && originalMealPlan) {
            onRevert(originalMealPlan);
          }
          if (flash) {
            const errorMessage = error.message || "Failed to move meal. Please try again.";
            flash("error", errorMessage);
          }
        })
        .finally(() => {
          if (submit) {
            submit(`/actions/common/refresh`, { method: "POST" });
          }
        });
    };

    // With DragOverlay, the original element stays in DOM (hidden), so we can
    // update immediately - dnd-kit animates the overlay, not the original element
    runUpdateAndApi();
  };
};
